import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader, Brain, ArrowRight, Home, MessageCircle, Video } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, success, failed, error
  const [retryCount, setRetryCount] = useState(0);
  const [trainingData, setTrainingData] = useState(null);

  const orderId = searchParams.get("order_id");

  useEffect(() => {
    if (!orderId) {
      setStatus("error");
      return;
    }

    const verify = async () => {
      try {
        const res = await axios.post(`${API}/payments/verify`, { order_id: orderId });
        if (res.data.paid) {
          setStatus("success");
          setTrainingData(res.data.training);
        } else if (res.data.status === "ACTIVE" && retryCount < 5) {
          // Payment still processing, retry
          setTimeout(() => setRetryCount(c => c + 1), 3000);
        } else {
          setStatus("failed");
        }
      } catch {
        setStatus("error");
      }
    };

    verify();
  }, [orderId, retryCount]);

  return (
    <div className="marketing-bg min-h-screen flex flex-col items-center justify-center px-6">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center">
          <div className="flex items-center gap-2">
            <Brain size={18} className="text-[#00F0FF]" />
            <span className="font-outfit font-bold text-[#00F0FF]">ShansoAI<span className="text-white">Hub</span></span>
          </div>
        </div>
      </nav>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-zinc-900/80 border border-zinc-800 p-10 text-center"
      >
        {status === "loading" && (
          <>
            <div className="w-20 h-20 border-4 border-[#00F0FF]/30 border-t-[#00F0FF] rounded-full animate-spin mx-auto mb-6" />
            <h2 className="font-outfit font-bold text-white text-2xl mb-3">Verifying Payment</h2>
            <p className="text-[#A1A1AA] font-dm-sans text-sm">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === "success" && (
          <>
            <motion.div
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            >
              <CheckCircle size={72} className="text-[#00F0FF] mx-auto mb-6" strokeWidth={1.5} />
            </motion.div>
            <h2 data-testid="payment-success-title" className="font-outfit font-black text-white text-3xl mb-3">Payment Successful!</h2>
            <p className="text-[#A1A1AA] font-dm-sans text-sm leading-relaxed mb-6">
              Congratulations! Your registration has been confirmed. Check your email for the Zoom meeting link and further details.
            </p>
            
            {/* Training Info */}
            {trainingData && (
              <div className="bg-zinc-800/50 border border-zinc-700 p-4 mb-6 text-left">
                <h3 className="text-white font-outfit font-bold text-sm mb-2">{trainingData.title}</h3>
                <div className="flex flex-col gap-1 text-xs text-[#A1A1AA] font-dm-sans">
                  <span>📅 {trainingData.date}</span>
                  <span>🕐 {trainingData.time}</span>
                </div>
              </div>
            )}
            
            <div className="bg-[#00F0FF]/10 border border-[#00F0FF]/20 p-4 mb-6 text-left">
              <p className="text-[#A1A1AA] text-xs font-dm-sans">Order ID</p>
              <p className="text-[#00F0FF] font-mono-code text-sm font-bold mt-1">{orderId}</p>
            </div>
            
            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Zoom Link Button */}
              {trainingData?.zoom_link && (
                <a
                  href={trainingData.zoom_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="join-zoom-btn"
                  className="w-full bg-[#2563eb] text-white font-outfit font-bold py-3.5 flex items-center justify-center gap-2 hover:bg-[#1d4ed8] transition-colors"
                >
                  <Video size={18} /> Join Zoom Meeting
                </a>
              )}
              
              {/* WhatsApp Community Button */}
              {trainingData?.whatsapp_community_link && (
                <a
                  href={trainingData.whatsapp_community_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="join-whatsapp-btn"
                  className="w-full bg-[#25d366] text-white font-outfit font-bold py-3.5 flex items-center justify-center gap-2 hover:bg-[#1fb855] transition-colors"
                >
                  <MessageCircle size={18} /> Join WhatsApp Community
                </a>
              )}
              
              <button
                data-testid="go-home-btn"
                onClick={() => navigate("/")}
                className="w-full bg-[#00F0FF] text-black font-outfit font-black py-3.5 flex items-center justify-center gap-2 hover:bg-white transition-colors"
              >
                <Home size={18} /> Go to Home
              </button>
            </div>
          </>
        )}

        {status === "failed" && (
          <>
            <XCircle size={72} className="text-red-400 mx-auto mb-6" strokeWidth={1.5} />
            <h2 data-testid="payment-failed-title" className="font-outfit font-black text-white text-3xl mb-3">Payment Not Completed</h2>
            <p className="text-[#A1A1AA] font-dm-sans text-sm leading-relaxed mb-8">
              Your payment was not completed. You can try registering again or contact us for assistance.
            </p>
            <button
              data-testid="try-again-btn"
              onClick={() => navigate("/")}
              className="w-full bg-zinc-800 border border-zinc-700 text-white font-outfit font-bold py-3.5 flex items-center justify-center gap-2 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"
            >
              <ArrowRight size={18} /> Browse Courses
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle size={72} className="text-zinc-500 mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="font-outfit font-black text-white text-3xl mb-3">Something Went Wrong</h2>
            <p className="text-[#A1A1AA] font-dm-sans text-sm leading-relaxed mb-8">
              We couldn't verify your payment. If you were charged, please contact support.
            </p>
            <button
              onClick={() => navigate("/")}
              className="w-full bg-zinc-800 border border-zinc-700 text-white font-outfit font-bold py-3.5 flex items-center justify-center gap-2 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"
            >
              <Home size={18} /> Go Home
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
